import { z } from "zod";
import { isNlsfbCode, NLSFB_SCHEME } from "./nlsfb.js";

/**
 * Kennisbank (docs/ASSETS-MJOP.md, "Kennisbank"): per NL-SfB-code levensduur en vervangingsprijs.
 * Platformbreed; alleen platformbeheer schrijft. Bedragen in centen, excl. btw.
 */
export const LIBRARY_STATUSES = ["empty", "concept", "reviewed"] as const;
export type LibraryStatus = (typeof LIBRARY_STATUSES)[number];
export const LIBRARY_STATUS_LABELS: Record<LibraryStatus, string> = {
  empty: "Leeg",
  concept: "Concept",
  reviewed: "Gecontroleerd",
};

export const LIBRARY_UNITS = ["st", "m2", "m1", "m3", "post"] as const;
export type LibraryUnit = (typeof LIBRARY_UNITS)[number];
export const LIBRARY_UNIT_LABELS: Record<LibraryUnit, string> = {
  st: "st",
  m2: "m²",
  m1: "m¹",
  m3: "m³",
  post: "post",
};

export const OBLIGATION_KINDS = ["keuring", "inspectie", "controle", "onderhoud", "overig"] as const;
export type ObligationKind = (typeof OBLIGATION_KINDS)[number];
export const OBLIGATION_KIND_LABELS: Record<ObligationKind, string> = {
  keuring: "Keuring",
  inspectie: "Inspectie",
  controle: "Controle",
  onderhoud: "Onderhoud",
  overig: "Overig",
};

/** Grondslag; alleen Wettelijk geeft spoed bij overschrijding (besluit 10). */
export const OBLIGATION_BASES = ["wettelijk", "zorgplicht", "verzekeraar", "eigen"] as const;
export type ObligationBasis = (typeof OBLIGATION_BASES)[number];
export const OBLIGATION_BASIS_LABELS: Record<ObligationBasis, string> = {
  wettelijk: "Wettelijk",
  zorgplicht: "Zorgplicht",
  verzekeraar: "Verzekeraar",
  eigen: "Eigen afspraak",
};

/** Vertaling tussen NL-SfB-versies. Alles behalve `exact` vraagt om controle. */
export const MAPPING_MATCHES = ["exact", "split", "merge", "unclear"] as const;
export type MappingMatch = (typeof MAPPING_MATCHES)[number];

/** Medianen uit de praktijk alleen vanaf zoveel organisaties, zonder namen. */
export const PRACTICE_MIN_ORGS = 3;

export interface LibraryElement {
  code: string;
  scheme: string;
  lifespanYears: number | null;
  replacementPriceCents: number | null;
  unit: LibraryUnit | null;
  priceLevelYear: number | null;
  status: LibraryStatus;
  sourceTitle: string | null;
  sourceUrl: string | null;
  notes: string | null;
  /** Telt op bij elke wijziging; assets onthouden welke versie ze kopieerden. */
  version: number;
  reviewedAt: string | null;
  reviewedBy: string | null;
  updatedAt: string;
}

export interface CodeMapping {
  fromScheme: string;
  fromCode: string;
  toScheme: string;
  toCode: string;
  match: MappingMatch;
}

/**
 * Pad van algemeen naar specifiek: "65.12" → ["6", "65", "65.1", "65.12"]. Werkt op de tekst van
 * de code, zodat een waarde op een hoger niveau ook geldt als het tussenniveau niet bestaat.
 */
export function libraryPath(code: string): string[] {
  const c = code.trim();
  if (!c) return [];
  const path = [c.slice(0, 1)];
  if (c.length >= 2) path.push(c.slice(0, 2));
  if (c.length >= 4) path.push(c.slice(0, 4));
  if (c.length >= 5) path.push(c);
  return path;
}

export interface Inherited<T> {
  value: T;
  /** Code waar de waarde staat; gelijk aan de gevraagde code als ze niet geërfd is. */
  from: string;
  /** Concept = voorstellen met het label *indicatief*. */
  indicative: boolean;
}

export interface ResolvedLibrary {
  code: string;
  scheme: string;
  /** Code in de versie van de kennisbank (na vertaling); null als die er niet is. */
  libraryCode: string | null;
  /** Vertaling niet eenduidig of ontbreekt → "Categorie mogelijk anders ingedeeld — controleer". */
  check: boolean;
  lifespanYears: Inherited<number> | null;
  /** Prijs, eenheid en prijspeil horen bij elkaar en erven samen. */
  replacement: Inherited<{ priceCents: number; unit: LibraryUnit | null; priceLevelYear: number | null }> | null;
}

export interface LibraryData {
  elements: readonly LibraryElement[];
  mappings?: readonly CodeMapping[];
}

/** Vertaalt een code uit een andere versie naar de versie van de kennisbank, langs het pad. */
function mapCode(
  code: string,
  scheme: string,
  target: string,
  mappings: readonly CodeMapping[],
): { code: string; match: MappingMatch } | null {
  const path = libraryPath(code).reverse();
  for (const c of path) {
    const hits = mappings.filter((m) => m.fromScheme === scheme && m.fromCode === c && m.toScheme === target);
    if (hits.length === 1) return { code: hits[0]!.toCode, match: c === code ? hits[0]!.match : "unclear" };
    if (hits.length > 1) return { code: hits[0]!.toCode, match: "split" };
  }
  return null;
}

/**
 * Zoekt in de kennisbank (docs/ASSETS-MJOP.md, "Opzoeken in de kennisbank"): per veld wint de
 * eerste ingevulde waarde van specifiek naar algemeen. Alleen binnen dezelfde versie; een andere versie loopt via de vertaaltabel.
 */
export function resolveLibrary(
  code: string,
  scheme: string,
  data: LibraryData,
  libraryScheme: string = NLSFB_SCHEME,
): ResolvedLibrary {
  let libCode: string | null = code;
  let check = false;
  if (scheme !== libraryScheme) {
    const m = mapCode(code, scheme, libraryScheme, data.mappings ?? []);
    libCode = m?.code ?? null;
    check = !m || m.match !== "exact";
  }
  const out: ResolvedLibrary = {
    code,
    scheme,
    libraryCode: libCode,
    check,
    lifespanYears: null,
    replacement: null,
  };
  if (!libCode) return out;

  const path = libraryPath(libCode);
  const elements = new Map(
    data.elements.filter((e) => e.scheme === libraryScheme && e.status !== "empty").map((e) => [e.code, e]),
  );
  for (const c of [...path].reverse()) {
    const e = elements.get(c);
    if (!e) continue;
    const indicative = e.status !== "reviewed";
    if (!out.lifespanYears && e.lifespanYears != null)
      out.lifespanYears = { value: e.lifespanYears, from: c, indicative };
    if (!out.replacement && e.replacementPriceCents != null)
      out.replacement = {
        value: { priceCents: e.replacementPriceCents, unit: e.unit, priceLevelYear: e.priceLevelYear },
        from: c,
        indicative,
      };
  }
  return out;
}

/** Heeft de kennisbank (geërfd of eigen) iets voor deze code? */
export const libraryHasData = (r: ResolvedLibrary) => Boolean(r.lifespanYears || r.replacement);

/** Mediaan; null bij een lege lijst. */
export function median(values: readonly number[]): number | null {
  if (!values.length) return null;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid]! : Math.round((s[mid - 1]! + s[mid]!) / 2);
}

// ── API ──

const year = z.number().int().min(1900).max(2200);
const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => v || null);
const url = z
  .string()
  .trim()
  .max(500)
  .transform((v) => v || null)
  .refine((v) => v === null || /^https?:\/\//i.test(v), "Gebruik een link die met http(s):// begint");

export const libraryElementBodySchema = z.object({
  lifespanYears: z.number().int().min(1).max(200).nullable(),
  replacementPriceCents: z.number().int().min(0).max(1_000_000_000).nullable(),
  unit: z.enum(LIBRARY_UNITS).nullable(),
  priceLevelYear: year.nullable(),
  status: z.enum(LIBRARY_STATUSES),
  sourceTitle: text(200).nullable(),
  sourceUrl: url.nullable(),
  notes: text(2000).nullable(),
});
export type LibraryElementBody = z.input<typeof libraryElementBodySchema>;

export const libraryImportBodySchema = z.object({
  /** .xlsx als base64. */
  file: z.string().min(1).max(8_000_000),
  /** Zonder bevestiging alleen het overzicht; er wordt niets geschreven. */
  confirm: z.boolean().default(false),
});

export const isLibraryCode = (code: string) => isNlsfbCode(code);

/** Een rij in de lijst van de kennisbank (platformbeheer). */
export interface LibraryListRow {
  code: string;
  label: string;
  level: 2 | 3 | 4;
  /** Assets met precies deze code, over alle organisaties. */
  assets: number;
  element: Pick<
    LibraryElement,
    "lifespanYears" | "replacementPriceCents" | "unit" | "priceLevelYear" | "status"
  > | null;
  /** Eigen gegevens op deze code (niet geërfd). */
  filled: boolean;
  /** Iets uit de kennisbank, eigen of geërfd. */
  covered: boolean;
}

export interface LibraryListResponse {
  scheme: string;
  rows: LibraryListRow[];
  totals: { codes: number; filled: number; assets: number; coveredAssets: number };
}

export interface PracticeStat {
  median: number;
  orgs: number;
}

export interface LibraryDetailResponse {
  code: string;
  label: string;
  scheme: string;
  element: LibraryElement | null;
  resolved: ResolvedLibrary;
  assets: number;
  /** Assets met deze code die een oudere versie uit de kennisbank gebruiken. */
  outdatedAssets: number;
  /** Uit de assets van organisaties; null onder PRACTICE_MIN_ORGS. */
  practice: { lifespanYears: PracticeStat | null; replacementPriceCents: PracticeStat | null };
}

export interface LibraryImportChange {
  sheet: "element";
  row: number;
  code: string;
  name?: string;
  kind: "new" | "changed" | "invalid";
  /** Gewijzigde velden of de reden van ongeldig. */
  detail: string;
}

export interface LibraryImportResult {
  applied: boolean;
  counts: { new: number; changed: number; unchanged: number; invalid: number };
  changes: LibraryImportChange[];
}
